import { FunctionComponent, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Box, CardContent, Card, Typography } from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";

import AppLogo from "@/assets/images/favicon.png";
import { useAppSelector } from "@/redux/hooks";
import { AuthError } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { InputField } from "@/components/InputField/InputField";

import { Button } from "@/components/Button/Button";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { useAuthTwoFactorCodeMutation } from "../../redux/auth.api";
import { TwoFactorAuthenticationSchema, TwoFactorAuthentication } from "./schema/twoFactorAuth";
import { authSelector } from "../../redux/auth.slice";

export const TwoFactorAuth: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`twoFA.${key}`);

	const { otpToken } = useAppSelector(authSelector);

	const initialValues: TwoFactorAuthentication = {
		code: "",
	};

	const [auth, { isLoading, error }] = useAuthTwoFactorCodeMutation();
	const {
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm({
		defaultValues: {
			code: "",
		},
		resolver: zodResolver(TwoFactorAuthenticationSchema),
	});

	const [currentFormState, setCurrentFormState] =
		useState<Partial<TwoFactorAuthentication>>(initialValues);

	const handleSaveFormState = (
		value: TwoFactorAuthentication[keyof TwoFactorAuthentication],
		name: keyof TwoFactorAuthentication
	) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const formErrors = Object.values(errors).map((error) => error.message) as ERROR_TYPE[];

	const onSubmit = async ({ code }: { code: string }) => {
		try {
			await auth({ code, token: otpToken ?? "" }).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<div className="w-full flex flex-col items-center">
			<div className="w-full z-10 flex flex-col justify-center items-center mb-8">
				<img alt="logo" className="w-20 h-20" src={AppLogo} />
				<div className="mb-2 text-md">Enter your 2FA code</div>
			</div>
			<Card
				elevation={16}
				sx={{
					mb: 4,
					position: "relative",
					width: "100%",
					maxWidth: "444px",
				}}
			>
				<CardContent>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Typography mb={4} variant="h6">
							{ts("title")}
						</Typography>

						<Box component="form" onSubmit={handleSubmit(onSubmit)}>
							<InputField
								error={!!errors.code?.message}
								handleChange={(event) => handleSaveFormState(event.target.value, "code")}
								name="code"
								placeholder={t("basics.code")}
								type="code"
								value={currentFormState.code || ""}
							/>
							<Button
								color={ButtonColor.ACTION}
								isLoading={isLoading}
								size={ButtonSize.ML}
								title={ts("cta")}
								type="submit"
							/>
						</Box>

						{getAllErrors(error, formErrors).length ? (
							<AuthError>{renderErrorMessages(getAllErrors(error, formErrors))}</AuthError>
						) : null}
					</Box>
				</CardContent>
			</Card>
		</div>
	);
};
